import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  VStack,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { LayoutOption, RecordingDevice, Settings } from "../Workbench";

interface SettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  settings: Settings;
  setSettings: (settings: Settings) => void;
}

const SettingsDialog = ({
  isOpen,
  onClose,
  settings,
  setSettings,
}: SettingsDialogProps) => {
  const saveSettings = useCallback((updatedSettings: Settings) => {
    localStorage.setItem("settings", JSON.stringify(updatedSettings));
  }, []);

  const handleChangeSchoolYear = useCallback(
    (_: string, valueAsNumber: number) => {
      if (Number.isNaN(valueAsNumber)) {
        return;
      }
      const updatedSettings: Settings = {
        ...settings,
        schoolYear: valueAsNumber,
      };
      setSettings(updatedSettings);
      saveSettings(updatedSettings);
    },
    [settings, setSettings, saveSettings]
  );

  const handleChangeSampleLength = useCallback(
    (_: string, valueAsNumber: number) => {
      if (Number.isNaN(valueAsNumber)) {
        return;
      }
      const updatedSettings: Settings = {
        ...settings,
        sampleLength: valueAsNumber,
      };
      setSettings(updatedSettings);
      saveSettings(updatedSettings);
    },
    [settings, setSettings, saveSettings]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack alignItems="flex-start" spacing={5}>
            <FormControl display="flex" alignItems="center">
              <FormLabel fontWeight="normal" mb={0}>
                Sample length (ms)
              </FormLabel>
              <NumberInput
                step={1}
                min={0}
                value={settings.sampleLength}
                onChange={handleChangeSampleLength}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel fontWeight="normal" mb={0}>
                School year
              </FormLabel>
              <NumberInput
                step={1}
                min={0}
                value={settings.schoolYear}
                onChange={handleChangeSchoolYear}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel>Location</FormLabel>
              <Input
                type="text"
                value={settings.location || ""}
                onChange={(e) => {
                  const updatedSettings: Settings = {
                    ...settings,
                    location: e.target.value,
                  };
                  setSettings(updatedSettings);
                  saveSettings(updatedSettings);
                }}
              />
              <FormHelperText>Enter location (e.g., county).</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>Recording device</FormLabel>
              <Select
                value={settings.recordingDevice}
                onChange={(e) => {
                  if (e.target.value) {
                    const updatedSettings: Settings = {
                      ...settings,
                      recordingDevice: e.target.value as RecordingDevice,
                    };
                    setSettings(updatedSettings);
                    saveSettings(updatedSettings);
                  }
                }}
              >
                <option value=""></option>
                <option value="microphone">Computer microphone</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Layout</FormLabel>
              <Select
                value={settings.layout}
                onChange={(e) => {
                  const updatedSettings: Settings = {
                    ...settings,
                    layout: e.target.value as LayoutOption,
                  };
                  setSettings(updatedSettings);
                  saveSettings(updatedSettings);
                }}
              >
                <option value="horizontal">Horizontal</option>
                <option value="vertical">Vertical</option>
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SettingsDialog;
